/**
 * BatchSigningModal — Modal for selecting multiple trades and sending
 * their contracts for batch signature via SignAPIs.
 *
 * Trades grouped by workflow state in collapsible sections.
 * AG Grid per section for proper column alignment.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';
import { AG_GRID_LOCALE_ES } from '@ag-grid-community/locale';
import { AG_GRID_LOCALE_PT } from '@ag-grid-community/locale';
import { rulesEngineService } from '../../services/api';
import { clientService } from '../../services/clientService';
import { logger } from '../../utils/logger';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import '../../styles/FilterButtons.css';
import './BatchSigningModal.css';

interface BatchSigningModalProps {
  isOpen: boolean;
  clientId: string;
  userEmail: string;
  isDesignatedSignatory: boolean;
  defaultDateFrom?: string;
  defaultDateTo?: string;
  onClose: () => void;
  onSuccess: (message: string) => void;
}

interface TradeWithRule {
  trade: any;
  ruleId: string;
  ruleName: string;
  action: Record<string, any>;
  signingConfigKey: string;  // Hash for grouping trades by same signing config
  signatoryNames: string;    // Display string e.g. "Juan Pérez, María López — Paralelo"
}

interface SentBatchGroup {
  groupId: string | null;  // null = individual (non-batch)
  trades: any[];
  sentAt: string;
  sentByName: string;
  status: string;  // Overall status: PENDING, SIGNED, etc.
}

/**
 * Build a key that uniquely identifies a signing configuration.
 * Trades with the same key can be batched together.
 */
function buildSigningConfigKey(action: Record<string, any>): string {
  const signatories = (action.signatories || []) as Array<{ email: string }>;
  const emails = signatories.map((s) => s.email.toLowerCase()).sort();
  const flow = (action.signatoryFlow || 'parallel').toLowerCase();
  const approvers = (action.approvers || []) as Array<{ email: string }>;
  const approverEmails = approvers.map((a) => a.email.toLowerCase()).sort();
  const approverFlow = (action.approverFlow || 'parallel').toLowerCase();
  const approverMoment = (action.approverMoment || 'before').toLowerCase();

  return [
    emails.join(','),
    flow,
    approverEmails.join(','),
    approverFlow,
    approverMoment,
  ].join('|');
}

function buildSignatoryDisplayString(action: Record<string, any>): string {
  const signatories = (action.signatories || []) as Array<{ fullName: string }>;
  const names = signatories.map((s) => s.fullName);
  const flow = (action.signatoryFlow || 'parallel').toLowerCase();
  const flowLabel = signatories.length > 1
    ? (flow === 'sequential' ? 'Secuencial' : 'Paralelo')
    : '';
  return flowLabel ? `${names.join(', ')} — ${flowLabel}` : names.join(', ');
}

const ELIGIBLE_WORKFLOW_STATES = ['Confirmed', 'ContractSigned', 'Final'];

const WORKFLOW_STATE_ORDER: Record<string, number> = {
  'Confirmed': 0,
  'ContractSigned': 1,
  'Final': 2,
};

const BatchSigningModal: React.FC<BatchSigningModalProps> = ({
  isOpen,
  clientId,
  userEmail,
  isDesignatedSignatory,
  defaultDateFrom,
  defaultDateTo,
  onClose,
  onSuccess,
}) => {
  const { t, i18n } = useTranslation();

  const agGridLocale = useMemo(() => {
    const lang = i18n.language?.substring(0, 2);
    if (lang === 'es') return AG_GRID_LOCALE_ES;
    if (lang === 'pt') return AG_GRID_LOCALE_PT;
    return undefined;
  }, [i18n.language]);

  // State
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitProgress, setSubmitProgress] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [eligibleTrades, setEligibleTrades] = useState<TradeWithRule[]>([]);
  const [sentBatches, setSentBatches] = useState<SentBatchGroup[]>([]);
  const [selectedTradeIds, setSelectedTradeIds] = useState<Set<string>>(new Set());

  // Filters
  const [selectedCounterparties, setSelectedCounterparties] = useState<Set<string>>(new Set());
  const [dateFrom, setDateFrom] = useState<string>(defaultDateFrom || '');
  const [dateTo, setDateTo] = useState<string>(defaultDateTo || '');

  // Collapsible sections — Confirmed expanded, others collapsed by default
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    new Set(['ContractSigned', 'Final', '__sent__']),
  );

  const toggleSection = useCallback((section: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  }, []);

  // ─── Load eligible trades ───────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const loadEligibleTrades = async () => {
      setLoading(true);
      setError(null);
      setSelectedTradeIds(new Set());
      setSelectedCounterparties(new Set());

      try {
        const allTrades = await clientService.getForwardTrades(
          clientId,
          dateFrom || undefined,
          dateTo || undefined,
        ) as any[];
        if (cancelled) return;

        // Separate eligible vs sent-for-signature trades
        const candidates: any[] = [];
        const sentTrades: any[] = [];

        for (const trade of allTrades) {
          const ws = trade.workflowState;
          if (!ELIGIBLE_WORKFLOW_STATES.includes(ws)) continue;
          const sigStatus = trade.SignatureStatus;
          if (sigStatus && sigStatus !== 'CANCELLED' && sigStatus !== 'REJECTED') {
            // Active signature — capture for "sent" section
            sentTrades.push(trade);
          } else {
            candidates.push(trade);
          }
        }

        // Group sent trades by batch groupId
        const batchMap = new Map<string, any[]>();
        for (const trade of sentTrades) {
          const key = trade.SignatureBatchGroupId || `individual_${trade.id}`;
          if (!batchMap.has(key)) batchMap.set(key, []);
          batchMap.get(key)!.push(trade);
        }

        const batchGroups: SentBatchGroup[] = Array.from(batchMap.entries()).map(([key, trades]) => ({
          groupId: key.startsWith('individual_') ? null : key,
          trades,
          sentAt: trades[0]?.SignatureSentAt || '',
          sentByName: trades[0]?.SignatureSentByName || '',
          status: trades.every((t: any) => t.SignatureStatus === 'SIGNED') ? 'SIGNED' : 'PENDING',
        }));

        // Sort: most recent first
        batchGroups.sort((a, b) => (b.sentAt > a.sentAt ? 1 : -1));

        if (!cancelled) setSentBatches(batchGroups);

        const results: TradeWithRule[] = [];

        for (const trade of candidates) {
          if (cancelled) return;

          const entity: Record<string, unknown> = {
            ProductType: trade.ProductType,
            IsAverageRateForward: trade.IsAverageRateForward,
            IsForwardStarting: trade.IsForwardStarting,
            SettlementType: trade.SettlementType,
            Direction: trade.Direction,
            CounterpartyName: trade.CounterpartyName,
            Currency1: trade.Currency1,
            Currency2: trade.Currency2,
            NotionalAmount: trade.QuantityCurrency1,
            ContraAmount: trade.QuantityCurrency2,
            TradeDate: trade.TradeDate,
            ValueDate: trade.ValueDate,
            MaturityDate: trade.MaturityDate,
            ParentTradeNumber: trade.ParentTradeNumber,
            IsInternalTrade: trade.IsInternalTrade,
            OurParty: trade.OurParty,
            Grouping1: trade.Grouping1,
            Grouping2: trade.Grouping2,
          };

          try {
            const response = await rulesEngineService.evaluate(clientId, {
              context: 'contract_signing',
              entity,
            });

            if (response.success && response.data?.matchedRules?.length > 0) {
              const matched = response.data.matchedRules[0];
              const action = matched.action as Record<string, any>;

              // Only include FX+ platform trades
              if (action.platform !== 'fx_plus') continue;

              const configKey = buildSigningConfigKey(action);
              const signatoryNames = buildSignatoryDisplayString(action);

              results.push({
                trade,
                ruleId: matched.ruleId,
                ruleName: matched.ruleName,
                action,
                signingConfigKey: configKey,
                signatoryNames,
              });
            }
          } catch (err) {
            logger.warn(`Failed to evaluate rules for trade ${trade.TradeNumber}:`, err);
          }
        }

        if (!cancelled) {
          setEligibleTrades(results);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t('batchSigning.loadFailed'));
          setLoading(false);
        }
      }
    };

    loadEligibleTrades();
    return () => { cancelled = true; };
  }, [isOpen, clientId, dateFrom, dateTo, t]);

  // ─── Derived data ───────────────────────────────────────────────

  const counterparties = useMemo(() => {
    const set = new Set<string>();
    eligibleTrades.forEach((tr) => {
      if (tr.trade.CounterpartyName) set.add(tr.trade.CounterpartyName);
    });
    return Array.from(set).sort();
  }, [eligibleTrades]);

  // Filter by counterparty (date filtering is done server-side via the API)
  const filteredTrades = useMemo(() => {
    if (selectedCounterparties.size === 0) return eligibleTrades;
    return eligibleTrades.filter((tr) => selectedCounterparties.has(tr.trade.CounterpartyName));
  }, [eligibleTrades, selectedCounterparties]);

  // Group by workflow state, ordered: Confirmed → ContractSigned → Final
  const groupedTrades = useMemo(() => {
    const groups: Record<string, TradeWithRule[]> = {};
    filteredTrades.forEach((tr) => {
      const ws = tr.trade.workflowState || 'Unknown';
      if (!groups[ws]) groups[ws] = [];
      groups[ws].push(tr);
    });
    return Object.entries(groups)
      .sort(([a], [b]) => (WORKFLOW_STATE_ORDER[a] ?? 99) - (WORKFLOW_STATE_ORDER[b] ?? 99));
  }, [filteredTrades]);

  // Selected rows derived from IDs
  const selectedRows = useMemo(() => {
    return filteredTrades.filter((tr) => selectedTradeIds.has(tr.trade.id));
  }, [filteredTrades, selectedTradeIds]);

  // Locked signing config — derived from the first selected trade
  const lockedConfigKey = useMemo(() => {
    if (selectedRows.length === 0) return null;
    return selectedRows[0].signingConfigKey;
  }, [selectedRows]);

  // Selection summary
  const selectionSummary = useMemo(() => {
    const total = selectedRows.length;
    const withContract = selectedRows.filter((r) => r.trade.ContractUrl).length;
    const withoutContract = total - withContract;

    const canSignDirect = isDesignatedSignatory && total > 0 && selectedRows.every((r) => {
      const action = r.action;
      const signatories = (action.signatories || []) as Array<{ email: string }>;
      const approvers = (action.approvers || []) as Array<{ email: string }>;
      const hasBeforeApprovers = approvers.length > 0 && (action.approverMoment || 'before').toLowerCase() === 'before';
      const isSoleSigner = signatories.length === 1 && signatories[0].email.toLowerCase() === userEmail.toLowerCase();
      return isSoleSigner && !hasBeforeApprovers;
    });

    // Check if any selected trade's rule uses WhatsApp notification
    const hasWhatsappConfig = total > 0 && selectedRows.some((r) => {
      const sigs = (r.action.signatories || []) as Array<{ notifyWhatsapp?: boolean }>;
      return sigs.some((s) => s.notifyWhatsapp);
    });

    return { total, withContract, withoutContract, canSignDirect, hasWhatsappConfig };
  }, [selectedRows, userEmail, isDesignatedSignatory]);

  // ─── AG Grid column defs ──────────────────────────────────────

  /** Format YYYY-MM-DD or YYYY/MM/DD → DD/MM/YYYY */
  const formatDate = (val: any) => {
    if (!val || typeof val !== 'string') return '';
    const sep = val.includes('-') ? '-' : '/';
    const parts = val.split(sep);
    if (parts.length !== 3) return val;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  const columnDefs = useMemo<ColDef[]>(() => [
    {
      headerName: t('batchSigning.col_trade'),
      field: 'trade.TradeNumber',
      sortable: true,
    },
    {
      headerName: t('batchSigning.col_tradeDate'),
      field: 'trade.TradeDate',
      sortable: true,
      valueFormatter: (params: any) => formatDate(params.value),
    },
    {
      headerName: t('batchSigning.col_counterparty'),
      field: 'trade.CounterpartyName',
      sortable: true,
    },
    {
      headerName: t('batchSigning.col_product'),
      sortable: true,
      valueGetter: (params: any) => {
        const trade = params.data?.trade;
        if (!trade) return '';
        return trade.IsAverageRateForward ? 'Fwd Asiático' : (trade.ProductType || '');
      },
    },
    {
      headerName: t('batchSigning.col_direction'),
      field: 'trade.Direction',
      sortable: true,
    },
    {
      headerName: t('batchSigning.col_notional'),
      sortable: true,
      type: 'rightAligned',
      valueGetter: (params: any) => params.data?.trade?.QuantityCurrency1,
      valueFormatter: (params: any) => {
        if (!params.value) return '';
        return Number(params.value).toLocaleString('en-US', { maximumFractionDigits: 0 });
      },
    },
    {
      headerName: t('batchSigning.col_price'),
      sortable: true,
      type: 'rightAligned',
      valueGetter: (params: any) => params.data?.trade?.Price,
      valueFormatter: (params: any) => {
        if (!params.value) return '';
        return Number(params.value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
      },
    },
    {
      headerName: t('batchSigning.col_maturity'),
      field: 'trade.MaturityDate',
      sortable: true,
      valueFormatter: (params: any) => formatDate(params.value),
    },
    {
      headerName: t('batchSigning.col_settlement'),
      field: 'trade.SettlementType',
      sortable: true,
    },
    {
      headerName: t('batchSigning.col_contract'),
      width: 55,
      sortable: false,
      cellStyle: { textAlign: 'center' },
      headerClass: 'ag-header-center',
      cellRenderer: (params: any) => {
        const hasContract = !!params.data?.trade?.ContractUrl;
        return (
          <span style={{ color: hasContract ? '#28a745' : 'var(--accent-yellow)', fontSize: '14px' }}>
            {hasContract ? '✓' : '⚠'}
          </span>
        );
      },
    },
    {
      headerName: t('batchSigning.col_signingGroup'),
      field: 'signatoryNames',
      flex: 1,
      minWidth: 150,
      sortable: true,
    },
  ], [t]);

  // ─── Selection handlers ─────────────────────────────────────────

  const toggleCounterparty = useCallback((cp: string) => {
    setSelectedCounterparties((prev) => {
      const next = new Set(prev);
      if (next.has(cp)) next.delete(cp);
      else next.add(cp);
      return next;
    });
  }, []);

  const toggleTradeSelection = useCallback((tradeId: string) => {
    setSelectedTradeIds((prev) => {
      const next = new Set(prev);
      if (next.has(tradeId)) {
        next.delete(tradeId);
      } else {
        // Determine the locked config key from current selection
        const currentlySelected = eligibleTrades.filter((tr) => prev.has(tr.trade.id));
        const activeConfigKey = currentlySelected.length > 0 ? currentlySelected[0].signingConfigKey : null;
        const clickedTrade = eligibleTrades.find((tr) => tr.trade.id === tradeId);
        if (clickedTrade && activeConfigKey && clickedTrade.signingConfigKey !== activeConfigKey) {
          return prev; // reject — different signing config
        }
        next.add(tradeId);
      }
      return next;
    });
  }, [eligibleTrades]);

  const toggleSectionSelection = useCallback((trades: TradeWithRule[]) => {
    setSelectedTradeIds((prev) => {
      const next = new Set(prev);
      const sectionIds = new Set(trades.map((tr) => tr.trade.id));
      const allSelected = trades.every((tr) => prev.has(tr.trade.id));
      if (allSelected) {
        trades.forEach((tr) => next.delete(tr.trade.id));
      } else {
        // Determine the locked config key from current selection (excluding this section)
        const currentlySelected = eligibleTrades.filter((tr) => prev.has(tr.trade.id) && !sectionIds.has(tr.trade.id));
        const activeConfigKey = currentlySelected.length > 0 ? currentlySelected[0].signingConfigKey : null;
        for (const tr of trades) {
          if (activeConfigKey && tr.signingConfigKey !== activeConfigKey) continue; // skip mismatched
          next.add(tr.trade.id);
        }
      }
      return next;
    });
  }, [eligibleTrades]);

  // ─── Submit handlers ────────────────────────────────────────────

  const handleSendForSignature = async () => {
    const tradesToSign = selectedRows.filter((r) => r.trade.ContractUrl);
    if (tradesToSign.length === 0) {
      setError(t('batchSigning.noContractsSelected'));
      return;
    }

    setSubmitting(true);
    setError(null);
    setSubmitProgress(t('batchSigning.sending', { count: tradesToSign.length }));

    try {
      const ruleId = tradesToSign[0].ruleId;
      const tradeIds = tradesToSign.map((tr) => tr.trade.id);

      await clientService.batchSign(clientId, tradeIds, ruleId);

      const skipped = selectedRows.length - tradesToSign.length;
      let message = t('batchSigning.sentSuccess', { count: tradesToSign.length });
      if (skipped > 0) message += ` ${t('batchSigning.skipped', { count: skipped })}`;

      onSuccess(message);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('batchSigning.batchCreateFailed'));
    } finally {
      setSubmitting(false);
      setSubmitProgress('');
    }
  };

  const handleSignDirect = async () => {
    const tradesToSign = selectedRows.filter((r) => r.trade.ContractUrl);
    if (tradesToSign.length === 0) {
      setError(t('batchSigning.noContractsSelected'));
      return;
    }

    const signatureText = selectedRows[0].action.signatories?.[0]?.fullName || userEmail;

    setSubmitting(true);
    setError(null);
    setSubmitProgress(t('batchSigning.signing', { count: tradesToSign.length }));

    try {
      const ruleId = tradesToSign[0].ruleId;
      const tradeIds = tradesToSign.map((tr) => tr.trade.id);

      await clientService.batchSignDirect(clientId, tradeIds, ruleId, signatureText);

      const skipped = selectedRows.length - tradesToSign.length;
      let message = t('batchSigning.signedSuccess', { count: tradesToSign.length });
      if (skipped > 0) message += ` ${t('batchSigning.skipped', { count: skipped })}`;

      onSuccess(message);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('batchSigning.batchSignFailed'));
    } finally {
      setSubmitting(false);
      setSubmitProgress('');
    }
  };

  // ─── Render ─────────────────────────────────────────────────────

  if (!isOpen) return null;

  return (
    <div className="batch-signing-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="batch-signing-modal">
        {/* Header */}
        <div className="batch-signing-header">
          <h3>{t('batchSigning.title')}</h3>
          <button className="batch-signing-close" onClick={onClose} disabled={submitting}>×</button>
        </div>

        {/* Error */}
        {error && <div className="batch-signing-error">{error}</div>}

        {/* Filter bar */}
        {!loading && (
          <div className="batch-signing-filters">
            <span className="filter-label">{t('batchSigning.counterparty')}:</span>
            {counterparties.map((cp) => (
              <button
                key={cp}
                className={`filter-btn ${selectedCounterparties.has(cp) ? 'active' : ''}`}
                onClick={() => toggleCounterparty(cp)}
              >
                {cp}
              </button>
            ))}
            {selectedCounterparties.size > 0 && (
              <button
                className="action-btn clear-btn"
                onClick={() => setSelectedCounterparties(new Set())}
              >
                {t('batchSigning.clear')}
              </button>
            )}
            <span style={{ marginLeft: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span className="filter-label">{t('batchSigning.tradeDate')}:</span>
              <input
                type="date"
                className="date-filter-input"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
              <span className="filter-label">{t('batchSigning.to')}</span>
              <input
                type="date"
                className="date-filter-input"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </span>
            <span style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--text-secondary)' }}>
              {t('batchSigning.eligibleTrades', { count: filteredTrades.length })}
            </span>
          </div>
        )}

        {/* Content — collapsible sections by workflow state */}
        <div className="batch-signing-content">
          {loading ? (
            <div className="batch-signing-loading">
              <div className="spinner" />
              <span>{t('batchSigning.evaluatingRules')}</span>
            </div>
          ) : submitting ? (
            <div className="batch-signing-loading">
              <div className="spinner" />
              <span>{submitProgress}</span>
              <p style={{ fontSize: '0.85rem', color: '#94a3b8', marginTop: '0.5rem' }}>
                {t('batchSigning.signingPatience')}
              </p>
            </div>
          ) : filteredTrades.length === 0 && sentBatches.length === 0 ? (
            <div className="batch-signing-loading">
              <span>{t('batchSigning.noEligibleTrades')}</span>
            </div>
          ) : (
            <div className="batch-signing-sections">
              {/* Eligible trades grouped by workflow state */}
              {groupedTrades.map(([ws, trades]) => {
                const isCollapsed = collapsedSections.has(ws);
                const allSelected = trades.every((tr) => selectedTradeIds.has(tr.trade.id));

                return (
                  <div key={ws} className="batch-section">
                    {/* Section header */}
                    <div
                      className="batch-section-header"
                      onClick={() => toggleSection(ws)}
                    >
                      <span className="chevron">{isCollapsed ? '▶' : '▼'}</span>
                      <span className="section-title">
                        {t(`batchSigning.ws_${ws}`, ws)}
                      </span>
                      <span className="section-count">
                        ({t('batchSigning.sectionCount', { count: trades.length })})
                      </span>
                      {!isCollapsed && (
                        <span
                          className="section-select-all"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSectionSelection(trades);
                          }}
                        >
                          {allSelected ? t('batchSigning.deselectAll') : t('batchSigning.selectAll')}
                        </span>
                      )}
                    </div>
                    {/* AG Grid for this section */}
                    {!isCollapsed && (
                      <div
                        className="ag-theme-alpine-dark batch-section-grid"
                      >
                        <AgGridReact
                          localeText={agGridLocale}
                          rowData={trades}
                          columnDefs={columnDefs}
                          domLayout="autoHeight"
                          suppressMovableColumns={true}
                          suppressCellFocus={true}
                          getRowId={(params) => params.data.trade.id}
                          rowClassRules={{
                            'batch-row-selected': (params: any) => selectedTradeIds.has(params.data?.trade?.id),
                            'batch-row-disabled': (params: any) =>
                              lockedConfigKey != null && params.data?.signingConfigKey !== lockedConfigKey,
                          }}
                          onRowClicked={(event) => {
                            if (event.data?.trade?.id) {
                              toggleTradeSelection(event.data.trade.id);
                            }
                          }}
                          defaultColDef={{
                            sortable: false,
                            filter: false,
                            resizable: true,
                            suppressMenu: true,
                          }}
                          onFirstDataRendered={(p) => p.api.autoSizeAllColumns()}
                          animateRows={false}
                        />
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Sent for signature section */}
              {sentBatches.length > 0 && (
                <div className="batch-section batch-section-sent">
                  <div
                    className="batch-section-header"
                    onClick={() => toggleSection('__sent__')}
                  >
                    <span className="chevron">{collapsedSections.has('__sent__') ? '▶' : '▼'}</span>
                    <span className="section-title">
                      {t('batchSigning.sentSectionTitle')}
                    </span>
                    <span className="section-count">
                      ({t('batchSigning.sectionCount', {
                        count: sentBatches.reduce((sum, b) => sum + b.trades.length, 0),
                      })})
                    </span>
                  </div>
                  {!collapsedSections.has('__sent__') && (
                    <div className="batch-sent-list">
                      {sentBatches.map((batch, idx) => (
                        <div key={batch.groupId || idx} className="batch-sent-group">
                          <div className="batch-sent-group-header">
                            <span className={`batch-sent-status ${batch.status.toLowerCase()}`}>
                              {batch.status === 'SIGNED' ? '✓' : '⏳'} {String(t(`batchSigning.status_${batch.status}`, { defaultValue: batch.status }))}
                            </span>
                            <span className="batch-sent-meta">
                              {batch.sentByName} — {(() => {
                                const sa: any = batch.sentAt;
                                if (!sa) return '';
                                if (typeof sa === 'string') return formatDate(sa.slice(0, 10));
                                if (sa.toDate) return formatDate(sa.toDate().toISOString().slice(0, 10));
                                if (sa.seconds) return formatDate(new Date(sa.seconds * 1000).toISOString().slice(0, 10));
                                return '';
                              })()}
                            </span>
                            {batch.trades.length > 1 && (
                              <span className="batch-sent-badge">
                                {t('batchSigning.batchLabel', { count: batch.trades.length })}
                              </span>
                            )}
                          </div>
                          <div className="batch-sent-trades">
                            {batch.trades.map((trade: any) => (
                              <div key={trade.id} className="batch-sent-trade-row">
                                <span className="trade-number">{trade.TradeNumber}</span>
                                <span className="counterparty">{trade.CounterpartyName}</span>
                                <span className="product">
                                  {trade.IsAverageRateForward ? 'Fwd Asiático' : trade.ProductType}
                                </span>
                                <span className="notional">
                                  {Number(trade.QuantityCurrency1 || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                                </span>
                                <span className={`sig-status ${(trade.SignatureStatus || '').toLowerCase()}`}>
                                  {String(t(`batchSigning.status_${trade.SignatureStatus}`, { defaultValue: trade.SignatureStatus }))}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer: summary + actions */}
        <div className="batch-signing-footer">
          {/* Selection summary — above buttons */}
          {!loading && !submitting && selectedRows.length > 0 && (
            <div className="batch-signing-summary">
              <span>
                <span className="count">{selectionSummary.total}</span> {t('batchSigning.tradesSelected')}
              </span>
              {selectionSummary.withoutContract > 0 && (
                <span className="warning">
                  ⚠ {t('batchSigning.missingContracts', { count: selectionSummary.withoutContract })}
                </span>
              )}
            </div>
          )}

          {/* WhatsApp warning — batch API doesn't support WhatsApp notifications */}
          {!loading && !submitting && !selectionSummary.canSignDirect && selectionSummary.hasWhatsappConfig && (
            <div className="batch-signing-whatsapp-warning">
              ⚠ {t('batchSigning.whatsappBatchWarning')}
            </div>
          )}

          {/* Action buttons */}
          <div className="batch-signing-actions">
            <button
              className="batch-btn cancel"
              onClick={onClose}
              disabled={submitting}
            >
              {t('batchSigning.cancel')}
            </button>
            <div className="spacer" />
            {selectionSummary.canSignDirect ? (
              <button
                className="batch-btn sign-direct"
                onClick={handleSignDirect}
                disabled={submitting || selectionSummary.withContract === 0}
              >
                {t('batchSigning.signBatch', { count: selectionSummary.withContract })}
              </button>
            ) : (
              <button
                className="batch-btn send"
                onClick={handleSendForSignature}
                disabled={submitting || selectionSummary.withContract === 0}
              >
                {t('batchSigning.sendBatch', { count: selectionSummary.withContract })}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BatchSigningModal;
